import {
  AllSplashScreens,
  changeAllSdbSplashScreensStatus,
  parseFueFlags,
} from "@store/user/user-slice-helper";
import { SplashScreenSdbTopic } from "@components/splash-screen/splash-screen-data";
import { SplashScreenViewStatus } from "@components/splash-screen/splash-screen-utils";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  fetchCurrentUser,
  getLoggedInUser,
  logoutUser,
  setFueFlags,
  updateCurrentUserProfile,
} from "@store/user/user-slice-thunk";

/**
 * State of User members
 */
export interface UserState {
  /** Current logged in user */
  currentUser: FaroCoreApiTypes.CompanyMemberBase | null;

  /** All the splash screens with their view status */
  splashScreens: AllSplashScreens;

  /**
   * Extra information about current logged in user.
   * For details like name, email, etc. use currentUser.
   * This is only used for information like login method, SSO provider, etc.
   */
  loggedInUser: SphereDashboardAPITypes.IGetLoggedInUserResponse | null;

  /**
   * If the user requests to update their email, this will be set to the new email.
   * This is used because email is not changed automatically and should be validated first.
   * But if we don't show the new email in the current session, the user might think that the email was not changed.
   */
  updatedEmail: undefined | string;

  /** Collects all the fetching properties for this slice */
  fetching: {
    /** Flag whether the current user is being fetched from the backend */
    isFetchingCurrentUser: boolean;

    /** Flag whether the logged in user is being fetched from the backend */
    isFetchingLoggedInUser: boolean;

    /** Flag whether the Fue flags are being set to the backend */
    isSettingFueFlags: boolean;

    /** Flag whether the user clicked on logging out, and the logout route is being called */
    isLoggingOut: boolean;
  };
}

const initialState: UserState = {
  currentUser: null,
  loggedInUser: null,

  // At initial state, all splash screens are unseen. The status will update based on the fetched data from backend
  splashScreens: { sdb: changeAllSdbSplashScreensStatus("unseen"), other: {} },

  updatedEmail: undefined,
  fetching: {
    isFetchingCurrentUser: false,
    isFetchingLoggedInUser: false,
    isSettingFueFlags: false,
    isLoggingOut: false,
  },
};

/**
 * Slice to access state of loaded user members
 */
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCurrentUser(
      state,
      action: PayloadAction<SphereDashboardAPITypes.ICompanyMemberBase>
    ) {
      state.currentUser = action.payload;
    },

    setUpdatedEmail(state, action: PayloadAction<string>) {
      state.updatedEmail = action.payload;
    },

    /** Set the status of the splashscreen topic to the provided one */
    setSplashScreenStatus(
      state,
      action: PayloadAction<{
        topic: SplashScreenSdbTopic;
        status: SplashScreenViewStatus;
      }>
    ) {
      state.splashScreens.sdb[action.payload.topic] = action.payload.status;
    },

    /** Reset the status of all the splashscreen topics to unseen */
    resetSplashScreenStatus(state) {
      state.splashScreens.sdb = changeAllSdbSplashScreensStatus("unseen");
    },

    resetUserState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.fetching.isFetchingCurrentUser = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.fetching.isFetchingCurrentUser = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.fetching.isFetchingCurrentUser = false;
      })

      .addCase(getLoggedInUser.pending, (state) => {
        state.fetching.isFetchingLoggedInUser = true;
      })
      .addCase(getLoggedInUser.fulfilled, (state, action) => {
        state.loggedInUser = action.payload;
        state.fetching.isFetchingLoggedInUser = false;

        state.splashScreens = parseFueFlags(
          state.splashScreens,
          action.payload.data.fueFlags
        );
      })
      .addCase(getLoggedInUser.rejected, (state) => {
        state.fetching.isFetchingLoggedInUser = false;
      })

      .addCase(setFueFlags.pending, (state) => {
        state.fetching.isSettingFueFlags = true;
      })
      .addCase(setFueFlags.fulfilled, (state, action) => {
        state.fetching.isSettingFueFlags = false;

        state.splashScreens = parseFueFlags(
          state.splashScreens,
          action.payload.updatedValue
        );
      })
      .addCase(setFueFlags.rejected, (state, action) => {
        state.fetching.isSettingFueFlags = false;
        throw new Error(getErrorDisplayMarkup(action.error));
      })

      .addCase(updateCurrentUserProfile.fulfilled, (state, action) => {
        if (state.currentUser) {
          state.currentUser = {
            ...state.currentUser,
            email: action.payload.mailAddress,
            firstName: action.payload.name,
            lastName: action.payload.lastName,
            thumbnailUrl: action.payload.profileImageUrl,
          };
        }
        if (state.loggedInUser) {
          state.loggedInUser.data = action.payload;
        }
      })
      .addCase(updateCurrentUserProfile.rejected, (_, action) => {
        throw new Error(getErrorDisplayMarkup(action.error));
      })

      .addCase(logoutUser.pending, (state) => {
        state.fetching.isLoggingOut = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.fetching.isLoggingOut = false;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.fetching.isLoggingOut = false;
      });
  },
});

export const {
  setCurrentUser,
  setUpdatedEmail,
  setSplashScreenStatus,
  resetSplashScreenStatus,
  resetUserState,
} = userSlice.actions;

export const userReducer = userSlice.reducer;
